import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Settings as SettingsIcon,
  Globe,
  Building2,
  Calendar,
  Clock,
  Link as LinkIcon,
  ListTodo,
  Webhook,
  CheckCircle,
  MapPin,
  Phone,
  Hash,
  Landmark,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Check,
  ChevronsUpDown,
  FileCheck,
  ClipboardList,
  CreditCard,
  Pencil,
} from 'lucide-react';
import { BillingDashboard } from '@/components/billing/BillingDashboard';
import { StripeProvider } from '@/components/billing/StripeProvider';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { getTimezone, cn, getTimeZonesList } from '@/lib/utils';
import { clinicService } from '@/services/clinicService';
import { stateService } from '@/services/stateService';
import { State } from '@/models/state.model';

const VALID_TABS = ['preferences', 'clinic', 'booking', 'billing', 'integrations'];

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clinic, isPractitioner, isAdmin, isClinicStaff, setClinic, isGuided } = useClinic();
  const { updateUserClinic } = useAuth();
  const { toast } = useToast();

  // Get base path based on current portal
  const getBasePath = () => {
    if (location.pathname.startsWith('/practitioner-portal')) {
      return '/practitioner-portal';
    }
    // Admin + clinic_staff both live at root.
    return '';
  };

  const basePath = getBasePath();

  // Timezone state
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Clinic edit state
  const [editClinicOpen, setEditClinicOpen] = useState(false);
  const [isSavingClinic, setIsSavingClinic] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [clinicForm, setClinicForm] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    primary_phone: '',
    fax: '',
    email: '',
    description: '',
  });

  useEffect(() => {
    stateService.getAllStates().then(res => {
      if (res.data?.states) setStates(res.data.states);
    });
  }, []);

  // Close any open sheets/dialogs on route change to prevent orphaned overlays (P1-3)
  useEffect(() => {
    setEditClinicOpen(false);
  }, [location.pathname]);

  const openEditClinic = () => {
    setClinicForm({
      name: clinic?.name ?? '',
      address_line_1: clinic?.address_line_1 ?? '',
      address_line_2: clinic?.address_line_2 ?? '',
      city: clinic?.city ?? '',
      state: clinic?.stateInfo?.id ?? clinic?.state ?? '',
      postal_code: clinic?.postal_code ?? '',
      primary_phone: clinic?.primary_phone ?? '',
      fax: clinic?.fax ?? '',
      email: clinic?.email ?? '',
      description: clinic?.description ?? '',
    });
    setEditClinicOpen(true);
  };

  const handleSaveClinic = async () => {
    if (!clinic?.id) return;
    setIsSavingClinic(true);
    try {
      const res = await clinicService.updateClinicInfo(clinic.id, clinicForm);
      const updatedClinic = res?.data?.clinic ?? res?.clinic ?? res;
      setClinic(updatedClinic);
      updateUserClinic(clinic.id, { name: updatedClinic?.name ?? clinicForm.name });
      setEditClinicOpen(false);
      toast({ title: 'Success', description: 'Clinic information updated.', variant: 'success' });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.error?.message ?? 'Failed to update clinic information.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingClinic(false);
    }
  };

  // Get active tab from URL query params, default to 'preferences'
  const tabFromUrl = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabFromUrl || '') ? tabFromUrl! : 'preferences';

  // Update URL when tab changes
  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
  };

  const timezoneOptions = getTimeZonesList().map(timezone => ({
    value: timezone.zone,
    label: timezone.text + ' (' + timezone.zone + ')',
  }));

  const filteredTimezones = timezoneOptions.filter(
    timezone =>
      timezone.label.toLowerCase().includes(searchValue.toLowerCase()) ||
      timezone.value.toLowerCase().includes(searchValue.toLowerCase())
  );

  const currentTimezone = getTimezone();

  const accountType = clinic?.paymentAccount?.metadata?.bank_account?.account_type as
    | string
    | undefined;
  const accountTypeLabel =
    accountType === 'BUSINESS_CHECKING'
      ? 'Business Checking'
      : accountType === 'BUSINESS_SAVINGS'
        ? 'Business Savings'
        : accountType === 'PERSONAL_CHECKING' || accountType === 'CHECKING'
          ? 'Personal Checking'
          : 'Personal Savings';

  useEffect(() => {
    const preSelectedTimezone = timezoneOptions.find(tz => tz.value === currentTimezone);
    setSelectedTimezone(preSelectedTimezone?.value as string);
  }, [currentTimezone]);

  const selectedTimezoneLabel =
    timezoneOptions.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone;

  const handleSaveTimezone = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      localStorage.setItem('userTimezone', selectedTimezone);

      toast({
        title: 'Success',
        description: 'Timezone updated successfully',
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save preferences',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation handlers
  const handleNavigation = (path: string) => {
    navigate(`${basePath}${path}`);
  };

  const handleBookingNavigation = () => {
    handleNavigation('/settings/booking');
  };

  const handleAvailabilityNavigation = () => {
    handleNavigation('/settings/availability');
  };

  const handleAppointmentTypesNavigation = () => {
    handleNavigation('/settings/appointment-types');
  };

  const handleCalendarNavigation = () => {
    handleNavigation('/settings/calendar');
  };

  const handleWebhooksNavigation = () => {
    handleNavigation('/settings/webhooks');
  };

  const handleConsentsNavigation = () => {
    handleNavigation('/settings/consents');
  };

  const handleIntakeFormsNavigation = () => {
    handleNavigation('/settings/intake-forms');
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='mb-2'>
        <div className='mb-2 flex items-center gap-3'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => navigate(`${basePath}/`)}
            className='gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Overview
          </Button>
        </div>
        <h1
          className='font-montserrat text-lg tracking-tight text-slate-900 sm:text-2xl'
          style={{ fontWeight: 700 }}
        >
          Settings
        </h1>
        <p className='mt-1 font-inter text-xs text-slate-600 sm:text-sm'>
          Manage your account settings, preferences, and clinic configuration
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
        <TabsList className='grid w-full grid-cols-2 sm:grid-cols-5 lg:inline-flex lg:w-auto'>
          <TabsTrigger value='preferences' className='text-xs sm:text-sm'>
            Preferences
          </TabsTrigger>
          <TabsTrigger value='clinic' className='text-xs sm:text-sm'>
            Clinic
          </TabsTrigger>
          {(isPractitioner) && (
            <TabsTrigger value='booking' className='text-xs sm:text-sm'>
              Booking
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value='billing' className='text-xs sm:text-sm'>
              <CreditCard className='mr-1.5 h-4 w-4' />
              Billing
            </TabsTrigger>
          )}
          {(isAdmin || isPractitioner) && (
            <TabsTrigger value='integrations' className='text-xs sm:text-sm'>
              Integrations
            </TabsTrigger>
          )}
        </TabsList>

        {/* User Preferences Tab */}
        <TabsContent value='preferences' className='mt-6 space-y-6'>
          <Card className='border-slate-200'>
            <CardContent className='p-6'>
              <div className='mb-6 flex items-center gap-3 border-b border-slate-100 pb-5'>
                <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100'>
                  <SettingsIcon className='h-5 w-5 text-slate-600' />
                </div>
                <div>
                  <h2 className='font-semibold text-slate-900'>User Preferences</h2>
                  <p className='text-sm text-slate-500'>Configure your account settings</p>
                </div>
              </div>

              {/* Timezone Setting */}
              <div className='space-y-4'>
                <div className='flex items-start gap-3'>
                  <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10'>
                    <Globe className='h-5 w-5 text-primary' />
                  </div>
                  <div className='flex-1'>
                    <Label htmlFor='timezone' className='text-sm font-medium text-slate-900'>
                      Timezone
                    </Label>
                    <p className='mt-0.5 text-sm text-slate-500'>
                      Set your preferred timezone for displaying dates and times
                    </p>
                  </div>
                </div>

                <div className='pl-13'>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        role='combobox'
                        aria-expanded={open}
                        className='h-11 w-full max-w-md justify-between border-slate-200 text-left hover:border-slate-300 hover:bg-slate-50'
                      >
                        <span className='truncate'>{selectedTimezoneLabel}</span>
                        <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 text-slate-400' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-full max-w-md p-0' align='start'>
                      <Command value={selectedTimezone}>
                        <CommandInput
                          placeholder='Search timezone...'
                          value={searchValue}
                          onValueChange={setSearchValue}
                          className='border-0'
                        />
                        <CommandList>
                          <CommandEmpty className='py-6 text-center text-sm text-slate-500'>
                            No timezone found.
                          </CommandEmpty>
                          <CommandGroup>
                            {filteredTimezones.map(timezone => (
                              <CommandItem
                                key={timezone.value}
                                value={timezone.value}
                                onSelect={currentValue => {
                                  setSelectedTimezone(
                                    currentValue === selectedTimezone ? currentValue : currentValue
                                  );
                                  setOpen(false);
                                  setSearchValue('');
                                }}
                                className='cursor-pointer'
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4 text-primary',
                                    selectedTimezone === timezone.value
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                                <span className='truncate'>{timezone.label}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Action Buttons */}
              <div className='mt-6 flex justify-end gap-3 border-t border-slate-100 pt-6'>
                <Button
                  onClick={handleSaveTimezone}
                  disabled={isLoading}
                  className='h-10 bg-primary px-5 text-white hover:bg-primary/90'
                >
                  {isLoading ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clinic Settings Tab */}
        <TabsContent value='clinic' className='mt-6 space-y-6'>
          {/* Bank Account - Only show if account is linked */}
          {/* {!!clinic?.paymentAccount && (
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>Bank Account</h3>
              <div className='overflow-hidden rounded-xl border border-slate-200 bg-white'>
                <div className='p-4'>
                  <p className='mb-3 text-xs text-slate-400'>
                    Where your payments will be deposited
                  </p>
                  <div className='flex flex-col gap-3 rounded-xl border border-emerald-200/60 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-teal to-emerald-600 shadow-sm sm:h-11 sm:w-11'>
                        <Landmark className='h-4 w-4 text-white sm:h-5 sm:w-5' />
                      </div>
                      <div className='min-w-0'>
                        <p className='text-sm font-semibold capitalize text-slate-900'>
                          {clinic?.paymentAccount?.bank_account_name}
                        </p>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          {accountTypeLabel} account ending in ••••{' '}
                          {clinic?.paymentAccount?.bank_account_last4}
                        </p>
                      </div>
                    </div>
                    <div className='flex w-fit items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5'>
                      <CheckCircle className='h-3.5 w-3.5 text-emerald-600' />
                      <span className='text-xs font-medium text-emerald-700'>Connected</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )} */}

          {/* Clinic Information */}
          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                Clinic Information
              </h3>
              {isAdmin && (
                <Button variant='outline' size='sm' className='h-8 gap-1.5 text-xs' onClick={openEditClinic}>
                  <Pencil className='h-3.5 w-3.5' />
                  Edit
                </Button>
              )}
            </div>
            <div className='overflow-hidden rounded-xl border border-slate-200 bg-white'>
              <div className='p-4'>
                <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                  {/* Clinic Name */}
                  <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                    <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                      <Building2 className='h-4 w-4 text-slate-600' />
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='text-xs font-medium text-slate-400'>Clinic Name</p>
                      <p className='truncate text-sm font-medium text-slate-900'>
                        {clinic?.name ?? '--'}
                      </p>
                    </div>
                  </div>

                  {/* NPI */}
                  <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                    <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                      <Hash className='h-4 w-4 text-slate-600' />
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='text-xs font-medium text-slate-400'>NPI</p>
                      <p className='font-mono text-sm font-medium text-slate-900'>
                        {clinic?.npi ?? '--'}
                      </p>
                    </div>
                  </div>

                  {/* Address */}
                  <div className='flex items-start space-x-3 rounded-lg border border-slate-200 p-3'>
                    <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                      <MapPin className='h-4 w-4 text-slate-600' />
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='text-xs font-medium text-slate-400'>Address</p>
                      <p className='text-sm text-slate-900'>
                        {clinic?.address_line_1 ?? '--'} {clinic?.address_line_2 ?? ''}
                      </p>
                      {(clinic?.city || clinic?.stateInfo?.name || clinic?.postal_code) && (
                        <p className='text-sm text-slate-900'>
                          {clinic?.city ?? ''}
                          {clinic?.city ? ', ' : ''}
                          {clinic?.stateInfo?.name ?? ''} {clinic?.postal_code ?? ''}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                    <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                      <Phone className='h-4 w-4 text-slate-600' />
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='text-xs font-medium text-slate-400'>Phone</p>
                      <p className='text-sm font-medium text-slate-900'>
                        {clinic?.primary_phone ?? '--'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Edit Clinic Sheet */}
          <Sheet open={editClinicOpen} onOpenChange={setEditClinicOpen}>
            <SheetContent className='flex w-full !max-w-[520px] flex-col gap-0 p-0'>
              <SheetHeader className='border-b border-slate-200 px-6 py-4'>
                <SheetTitle className='flex items-center gap-2 text-base font-semibold text-slate-900'>
                  <Building2 className='h-4 w-4 text-slate-500' />
                  Edit Clinic Information
                </SheetTitle>
              </SheetHeader>
              <div className='flex-1 overflow-y-auto px-6 py-6 space-y-4'>
                <div className='space-y-1'>
                  <Label className='text-xs text-slate-500'>Clinic Name <span className='text-red-500'>*</span></Label>
                  <Input required value={clinicForm.name} onChange={e => setClinicForm(f => ({ ...f, name: e.target.value }))} placeholder='Clinic name' />
                </div>
                <div className='space-y-1'>
                  <Label className='text-xs text-slate-500'>Email <span className='text-red-500'>*</span></Label>
                  <Input required value={clinicForm.email} onChange={e => setClinicForm(f => ({ ...f, email: e.target.value }))} placeholder='clinic@example.com' type='email' />
                </div>
                <div className='space-y-1'>
                  <Label className='text-xs text-slate-500'>Phone <span className='text-red-500'>*</span></Label>
                  <Input required value={clinicForm.primary_phone} onChange={e => setClinicForm(f => ({ ...f, primary_phone: e.target.value }))} placeholder='+1 (555) 000-0000' />
                </div>
                <div className='space-y-1'>
                  <Label className='text-xs text-slate-500'>Fax <span className='text-slate-400'>(optional)</span></Label>
                  <Input value={clinicForm.fax} onChange={e => setClinicForm(f => ({ ...f, fax: e.target.value }))} placeholder='Fax number' />
                </div>
                <div className='space-y-1'>
                  <Label className='text-xs text-slate-500'>Address Line 1 <span className='text-red-500'>*</span></Label>
                  <Input required value={clinicForm.address_line_1} onChange={e => setClinicForm(f => ({ ...f, address_line_1: e.target.value }))} placeholder='Street address' />
                </div>
                <div className='space-y-1'>
                  <Label className='text-xs text-slate-500'>Address Line 2 <span className='text-slate-400'>(optional)</span></Label>
                  <Input value={clinicForm.address_line_2} onChange={e => setClinicForm(f => ({ ...f, address_line_2: e.target.value }))} placeholder='Suite, unit, etc.' />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-1'>
                    <Label className='text-xs text-slate-500'>City <span className='text-red-500'>*</span></Label>
                    <Input required value={clinicForm.city} onChange={e => setClinicForm(f => ({ ...f, city: e.target.value }))} placeholder='City' />
                  </div>
                  <div className='space-y-1'>
                    <Label className='text-xs text-slate-500'>Postal Code <span className='text-red-500'>*</span></Label>
                    <Input required value={clinicForm.postal_code} onChange={e => setClinicForm(f => ({ ...f, postal_code: e.target.value }))} placeholder='00000' />
                  </div>
                </div>
                <div className='space-y-1'>
                  <Label className='text-xs text-slate-500'>State <span className='text-red-500'>*</span></Label>
                  <select
                    required
                    value={clinicForm.state}
                    onChange={e => setClinicForm(f => ({ ...f, state: e.target.value }))}
                    className='h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                  >
                    <option value=''>Select state</option>
                    {states.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className='space-y-1'>
                  <Label className='text-xs text-slate-500'>Description <span className='text-slate-400'>(optional)</span></Label>
                  <textarea
                    value={clinicForm.description}
                    onChange={e => setClinicForm(f => ({ ...f, description: e.target.value }))}
                    placeholder='Brief description'
                    rows={3}
                    className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none'
                  />
                </div>
              </div>
              <div className='flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4'>
                <Button variant='outline' onClick={() => setEditClinicOpen(false)} disabled={isSavingClinic}>Cancel</Button>
                <Button onClick={handleSaveClinic} disabled={isSavingClinic} className='bg-brand-teal text-white hover:bg-brand-teal/90'>
                  {isSavingClinic ? <><Loader2 className='mr-2 h-4 w-4 animate-spin' />Saving...</> : 'Save Changes'}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </TabsContent>

        {/* Booking & Scheduling Tab - Only show for practitioners */}
        {(isPractitioner) && (
          <TabsContent value='booking' className='mt-6 space-y-6'>
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                Booking & Scheduling
              </h3>
              <div className='overflow-hidden rounded-xl border border-slate-200 bg-white'>
                <div className='space-y-3 p-4'>
                  {/* Booking Dashboard */}
                  <button
                    onClick={handleBookingNavigation}
                    className='group flex w-full items-center justify-between rounded-lg border border-slate-200 p-4 transition-all hover:border-teal-300 hover:bg-teal-50/30 hover:shadow-sm'
                  >
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 shadow-sm'>
                        <LinkIcon className='h-5 w-5 text-white' />
                      </div>
                      <div className='text-left'>
                        <p className='text-sm font-semibold text-slate-900'>Booking Dashboard</p>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          Manage public booking link, availability & settings
                        </p>
                      </div>
                    </div>
                    <ArrowRight className='h-5 w-5 text-slate-400 transition-colors group-hover:text-teal-600' />
                  </button>

                  {/* Availability Settings */}
                  {/* <button
                    onClick={handleAvailabilityNavigation}
                    className='group flex w-full items-center justify-between rounded-lg border border-slate-200 p-4 transition-all hover:border-blue-300 hover:bg-blue-50/30 hover:shadow-sm'
                  >
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm'>
                        <Clock className='h-5 w-5 text-white' />
                      </div>
                      <div className='text-left'>
                        <p className='text-sm font-semibold text-slate-900'>
                          Availability Settings
                        </p>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          Set your weekly schedule and time off
                        </p>
                      </div>
                    </div>
                    <ArrowRight className='h-5 w-5 text-slate-400 transition-colors group-hover:text-blue-600' />
                  </button> */}

                  {/* Appointment Types */}
                  {/* <button
                    onClick={handleAppointmentTypesNavigation}
                    className='group flex w-full items-center justify-between rounded-lg border border-slate-200 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 hover:shadow-sm'
                  >
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 shadow-sm'>
                        <ListTodo className='h-5 w-5 text-white' />
                      </div>
                      <div className='text-left'>
                        <p className='text-sm font-semibold text-slate-900'>Appointment Types</p>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          Define appointment types patients can book
                        </p>
                      </div>
                    </div>
                    <ArrowRight className='h-5 w-5 text-slate-400 transition-colors group-hover:text-purple-600' />
                  </button> */}
                </div>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Billing Tab - Admin only */}
        {isAdmin && (
          <TabsContent value='billing' className='mt-6'>
            <StripeProvider>
              <BillingDashboard />
            </StripeProvider>
          </TabsContent>
        )}

        {/* Integrations Tab */}
        {(isPractitioner || isAdmin) && (<TabsContent value='integrations' className='mt-6 space-y-6'>
          {/* Calendar Integration - Only show for practitioners */}
          {(isPractitioner) && (
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                Calendar Integration
              </h3>
              <div className='overflow-hidden rounded-xl border border-slate-200 bg-white'>
                <div className='p-4'>
                  <button
                    onClick={handleCalendarNavigation}
                    className='group flex w-full items-center justify-between rounded-lg border border-slate-200 p-4 transition-all hover:border-slate-300 hover:shadow-sm'
                  >
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm'>
                        <Calendar className='h-5 w-5 text-white' />
                      </div>
                      <div className='text-left'>
                        <p className='text-sm font-semibold text-slate-900'>Calendar Integration</p>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          Connect Google Calendar or Outlook to sync availability
                        </p>
                      </div>
                    </div>
                    <ArrowRight className='h-5 w-5 text-slate-400 transition-colors group-hover:text-slate-600' />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Webhook Management - Only show for admins */}
          {isAdmin && (
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                Webhook Management
              </h3>
              <div className='overflow-hidden rounded-xl border border-slate-200 bg-white'>
                <div className='p-4'>
                  <button
                    onClick={handleWebhooksNavigation}
                    className='group flex w-full items-center justify-between rounded-lg border border-slate-200 p-4 transition-all hover:border-orange-300 hover:bg-orange-50/30 hover:shadow-sm'
                  >
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 shadow-sm'>
                        <Webhook className='h-5 w-5 text-white' />
                      </div>
                      <div className='text-left'>
                        <p className='text-sm font-semibold text-slate-900'>Webhook Management</p>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          Configure webhooks for real-time event notifications
                        </p>
                      </div>
                    </div>
                    <ArrowRight className='h-5 w-5 text-slate-400 transition-colors group-hover:text-orange-600' />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Consent Forms - Only show for admins; hidden in guided clinics */}
          {isAdmin && !isGuided && (
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                Forms
              </h3>
              <div className='overflow-hidden rounded-xl border border-slate-200 bg-white'>
                <div className='space-y-3 p-4'>
                  <button
                    onClick={handleConsentsNavigation}
                    className='group flex w-full items-center justify-between rounded-lg border border-slate-200 p-4 transition-all hover:border-emerald-300 hover:bg-emerald-50/30 hover:shadow-sm'
                  >
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-sm'>
                        <FileCheck className='h-5 w-5 text-white' />
                      </div>
                      <div className='text-left'>
                        <p className='text-sm font-semibold text-slate-900'>Consent Forms</p>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          Manage consent form templates for patient signatures
                        </p>
                      </div>
                    </div>
                    <ArrowRight className='h-5 w-5 text-slate-400 transition-colors group-hover:text-emerald-600' />
                  </button>

                  <button
                    onClick={handleIntakeFormsNavigation}
                    className='group flex w-full items-center justify-between rounded-lg border border-slate-200 p-4 transition-all hover:border-teal-300 hover:bg-teal-50/30 hover:shadow-sm'
                  >
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 shadow-sm'>
                        <ClipboardList className='h-5 w-5 text-white' />
                      </div>
                      <div className='text-left'>
                        <p className='text-sm font-semibold text-slate-900'>Intake Forms</p>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          Build custom pre-visit intake forms to send patients on booking
                        </p>
                      </div>
                    </div>
                    <ArrowRight className='h-5 w-5 text-slate-400 transition-colors group-hover:text-teal-600' />
                  </button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>)}
      </Tabs>
    </div>
  );
};

export default Settings;
